import { useState, useCallback, useEffect } from 'react';
import { trendlineStrategyService, getErrorMessage } from '../config/api';
import { ApiError } from '../utils/apiError';
import { transformToApiFormat, transformFromApiFormat } from '../utils/strategyUtils';

/**
 * Custom hook for managing trendline strategies
 * Provides CRUD operations, validation, and indicator schema fetching
 */
export const useTrendlineStrategy = () => {
  const [strategies, setStrategies] = useState([]);
  const [currentStrategy, setCurrentStrategy] = useState(null);
  const [indicatorSchemas, setIndicatorSchemas] = useState(null);
  const [rulesSchema, setRulesSchema] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  /**
   * Fetch all strategies
   */
  const fetchStrategies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await trendlineStrategyService.getAll();
      const transformed = (response.data || []).map(transformFromApiFormat);
      setStrategies(transformed);
      return transformed;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      console.error('Error fetching strategies:', err instanceof ApiError ? err.toLogObject() : err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch a single strategy by ID
   */
  const fetchStrategy = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await trendlineStrategyService.getById(id);
      const transformed = transformFromApiFormat(response.data);
      setCurrentStrategy(transformed);
      return transformed;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      console.error('Error fetching strategy:', err instanceof ApiError ? err.toLogObject() : err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new strategy
   * @param {Object} strategyData - Strategy form data
   * @returns {Object|null} Created strategy or null on error
   */
  const createStrategy = useCallback(async (strategyData) => {
    setLoading(true);
    setError(null);
    try {
      const apiData = transformToApiFormat(strategyData);
      const response = await trendlineStrategyService.create(apiData);
      const transformed = transformFromApiFormat(response.data);

      // Update local state
      setStrategies(prev => [...prev, transformed]);
      setCurrentStrategy(transformed);

      return transformed;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      console.error('Error creating strategy:', err instanceof ApiError ? err.toLogObject() : err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update an existing strategy
   * @param {string} id - Strategy ID
   * @param {Object} strategyData - Updated strategy data
   * @returns {Object|null} Updated strategy or null on error
   */
  const updateStrategy = useCallback(async (id, strategyData) => {
    setLoading(true);
    setError(null);
    try {
      const apiData = transformToApiFormat(strategyData);
      const response = await trendlineStrategyService.update(id, apiData);
      const transformed = transformFromApiFormat(response.data);

      // Update local state
      setStrategies(prev => prev.map(s => s.id === id ? transformed : s));
      setCurrentStrategy(transformed);

      return transformed;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      console.error('Error updating strategy:', err instanceof ApiError ? err.toLogObject() : err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete a strategy
   * @param {string} id - Strategy ID
   * @returns {boolean} Success status
   */
  const deleteStrategy = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await trendlineStrategyService.delete(id);

      // Update local state
      setStrategies(prev => prev.filter(s => s.id !== id));
      if (currentStrategy?.id === id) {
        setCurrentStrategy(null);
      }

      return true;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      console.error('Error deleting strategy:', err instanceof ApiError ? err.toLogObject() : err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [currentStrategy?.id]);

  /**
   * Validate a strategy without saving
   * @param {Object} strategyData - Strategy to validate
   * @returns {Object} Validation result { valid, errors, warnings, suggestions }
   */
  const validateStrategy = useCallback(async (strategyData) => {
    setLoading(true);
    setError(null);
    setValidationResult(null);
    try {
      const apiData = transformToApiFormat(strategyData);
      const response = await trendlineStrategyService.validate(apiData);
      const result = response.data;
      setValidationResult(result);
      return result;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      const result = {
        valid: false,
        errors: [errorMessage],
        warnings: [],
        suggestions: [],
      };
      setValidationResult(result);
      setError(errorMessage);
      console.error('Error validating strategy:', err instanceof ApiError ? err.toLogObject() : err);
      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch indicator schemas for building dynamic forms
   * @returns {Object|null} Indicator schemas
   */
  const fetchIndicatorSchemas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await trendlineStrategyService.getIndicatorSchemas();
      const schemas = response.data?.indicators || response.data || {};
      setIndicatorSchemas(schemas);
      return schemas;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      console.error('Error fetching indicator schemas:', err instanceof ApiError ? err.toLogObject() : err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch rules schema (rule types, comparison operators, logical operators)
   * @returns {Object|null} Rules schema
   */
  const fetchRulesSchema = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await trendlineStrategyService.getRulesSchema();
      const schema = response.data || {};

      // Transform conditionTypes from Array to Object for easier lookups
      if (schema.conditionTypes && Array.isArray(schema.conditionTypes)) {
        const ctMap = {};
        schema.conditionTypes.forEach(ct => {
          ctMap[ct.name] = ct;
        });
        schema.conditionTypes = ctMap;
      }

      setRulesSchema(schema);
      return schema;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      console.error('Error fetching rules schema:', err instanceof ApiError ? err.toLogObject() : err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear current error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear validation result
   */
  const clearValidation = useCallback(() => {
    setValidationResult(null);
  }, []);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setStrategies([]);
    setCurrentStrategy(null);
    setIndicatorSchemas(null);
    setRulesSchema(null);
    setLoading(false);
    setError(null);
    setValidationResult(null);
  }, []);

  return {
    // State
    strategies,
    currentStrategy,
    indicatorSchemas,
    rulesSchema,
    loading,
    error,
    validationResult,

    // Actions
    fetchStrategies,
    fetchStrategy,
    createStrategy,
    updateStrategy,
    deleteStrategy,
    validateStrategy,
    fetchIndicatorSchemas,
    fetchRulesSchema,

    // Utilities
    clearError,
    clearValidation,
    reset,
    setCurrentStrategy,
  };
};

/**
 * Hook for fetching indicator schemas on mount
 * Useful for components that need schema data immediately
 */
export const useIndicatorSchemas = () => {
  const [schemas, setSchemas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSchemas = async () => {
      try {
        const response = await trendlineStrategyService.getIndicatorSchemas();
        setSchemas(response.data?.indicators || response.data || {});
      } catch (err) {
        setError(getErrorMessage(err));
        console.error('Error fetching indicator schemas:', err instanceof ApiError ? err.toLogObject() : err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchemas();
  }, []);

  return { schemas, loading, error };
};

/**
 * Hook for fetching rules schema on mount
 * Returns rule types, comparison operators, and logical operators from the API
 * Falls back to hardcoded constants if API fails
 */
export const useRulesSchema = () => {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSchema = async () => {
      try {
        const response = await trendlineStrategyService.getRulesSchema();
        const data = response.data || {};

        // Transform conditionTypes from Array to Object for easier lookups
        if (data.conditionTypes && Array.isArray(data.conditionTypes)) {
          const ctMap = {};
          data.conditionTypes.forEach(ct => {
            ctMap[ct.name] = ct;
          });
          data.conditionTypes = ctMap;
        }

        setSchema(data);
      } catch (err) {
        setError(getErrorMessage(err));
        console.error('Error fetching rules schema:', err instanceof ApiError ? err.toLogObject() : err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchema();
  }, []);

  // Helper functions to get formatted options for dropdowns
  const getRuleTypeOptions = () => {
    if (!schema?.ruleTypes) return [];
    return schema.ruleTypes.map(rt => ({
      value: rt.name,
      label: rt.description,
      apiValue: rt.value,
    }));
  };

  const getComparisonOperatorOptions = () => {
    if (!schema?.comparisonOperators) return [];
    return schema.comparisonOperators.map(op => ({
      value: op.name,
      label: `${op.description} (${op.symbol})`,
      symbol: op.symbol,
    }));
  };

  const getLogicalOperatorOptions = () => {
    if (!schema?.logicalOperators) return [];
    return schema.logicalOperators.map(op => ({
      value: op.name,
      label: op.description,
      symbol: op.symbol,
    }));
  };

  return {
    schema,
    loading,
    error,
    getRuleTypeOptions,
    getComparisonOperatorOptions,
    getLogicalOperatorOptions,
  };
};

export default useTrendlineStrategy;
